/**
 * Product Info (New) — brand-new section JS. Doesn't touch
 * assets/main-product-custom.js or any other existing file.
 *
 * Gallery thumbnail-click swap is adapted from design-reference/js/main.js's
 * "PDP gallery: click a thumbnail to swap the main image" handler — same
 * click -> highlight thumbnail -> show matching main content behavior,
 * adapted to swap a real image/video/model slide instead of a placeholder
 * text label. The "About the Brand" accordion toggle is copied from the
 * same file's accordion handler, generalized to run for every .pdp-accordion
 * instance (the reference only had one).
 *
 * Variant matching / price update / add-to-cart is adapted from
 * assets/main-product-custom.js (getMatchingVariant, getSelectedOptions,
 * updatePrice, ajaxAddToCart, the /cart/add.js fetch, and the
 * document.dispatchEvent(new CustomEvent('cart:open', {bubbles:true}))
 * success signal that opens the existing cart drawer) — same approach,
 * same endpoint, same event. ONE DELIBERATE CHANGE, flagged in chat: the
 * original binds its variant "change" listener to the <form> element,
 * but the variant-picker radio inputs are rendered in a sibling element
 * outside the form (linked only via the HTML form="..." attribute) —
 * change events don't bubble through that attribute association, only
 * through real DOM ancestry, so that listener would never fire for them.
 * This binds to the whole section container instead, which is a genuine
 * ancestor of both the variant picker and the form.
 *
 * Variant-change also swaps the gallery to the variant's featured_media,
 * reusing the SAME swap function a thumbnail click uses (the reference has
 * no gallery-scroll mechanism to reuse; the existing section's gallery is
 * a scroll-to-element slider, a different paradigm that doesn't map onto
 * the reference's swap-based main+thumbnails UI).
 *
 * Mobile sticky CTA bar (initStickyVisibility/initVariants' sticky wiring):
 * the bar lives in the .liquid file as a SIBLING of the .pdp div, which is
 * the `root` every other function here is scoped to (it carries
 * data-product-info-section) — so sticky elements are looked up via a
 * shared data-section-id instead of root.querySelector. Its Buy button is
 * `type="button"` and calls form.requestSubmit() rather than being a second
 * submit button in a second form; that re-fires the SAME 'submit' listener
 * below, so add-to-cart stays one code path. "Go to checkout" works by
 * appending a transient hidden `return_to` input to the form immediately
 * before requestSubmit() (removed right after — FormData(form) already
 * snapshotted it synchronously inside the submit handler) — ajaxAddToCart
 * reads that field back out of the FormData it built and redirects on
 * success instead of dispatching cart:open.
 */
(function () {
  'use strict';

  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var formatted = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
    return (format || '${{amount}}').replace('{{amount}}', formatted);
  }

  function getSelectedOptions(root) {
    var inputs = root.querySelectorAll('.pdp-variants__input:checked');
    var selected = [];
    inputs.forEach(function (input) {
      selected.push(input.value);
    });
    return selected;
  }

  function getMatchingVariant(variants, selectedOptions) {
    if (!selectedOptions.length) return null;
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var matches = true;
      for (var j = 0; j < selectedOptions.length; j++) {
        if (variant.options[j] !== selectedOptions[j]) {
          matches = false;
          break;
        }
      }
      if (matches) return variant;
    }
    return null;
  }

  function updatePrice(root, variant, moneyFormat) {
    var priceBlock = root.querySelector('[data-price-block]');
    if (!priceBlock || !variant) return;
    priceBlock.setAttribute('data-price', variant.price);
    priceBlock.setAttribute('data-compare-price', variant.compare_at_price || 0);

    var current = priceBlock.querySelector('[data-price-current]');
    if (current) current.textContent = formatMoney(variant.price, moneyFormat);

    var compare = priceBlock.querySelector('[data-price-compare]');
    var showCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
    if (showCompare) {
      if (!compare) {
        compare = document.createElement('s');
        compare.setAttribute('data-price-compare', '');
        priceBlock.appendChild(compare);
      }
      compare.textContent = formatMoney(variant.compare_at_price, moneyFormat);
    } else if (compare) {
      compare.remove();
    }
  }

  function updateButtons(root, variant, soldOutLabel) {
    var atc = root.querySelector('[data-add-to-cart]');
    if (!atc) return;
    var available = variant && variant.available;
    atc.disabled = !available;
    if (!available) {
      atc.setAttribute('aria-disabled', 'true');
    } else {
      atc.removeAttribute('aria-disabled');
    }
    var label = available
      ? atc.getAttribute('data-label-available')
      : atc.getAttribute('data-label-soldout') || soldOutLabel;
    if (label) atc.textContent = label;
  }

  function updateVariantId(root, variant) {
    var input = root.querySelector('[data-variant-id]');
    if (input && variant) input.value = variant.id;
  }

  // `variant` is null both when the current variant is explicitly
  // unavailable AND when no variant matches the selected options at all —
  // both are treated as sold out here, per spec. Visibility only — never
  // sets `disabled` here (that's reserved for ajaxAddToCart's in-flight
  // loading state, a separate concern). A sold-out product's Buy button is
  // gone (via `hidden`), not grayed out.
  function updateStickyAvailability(stickyRoot, variant) {
    // Only the `standard` purchase state lets availability drive this bar. Every
    // other state was resolved server-side into a fixed single-CTA layout, and the
    // class toggle below would strip --soldout the moment an available variant got
    // selected, collapsing that layout into a 2-column grid holding one button.
    // Reading data-purchase-state (rather than a per-state boolean attribute) keeps
    // this in step with the Liquid: a state added later is inert here until it
    // explicitly opts in.
    if (stickyRoot.getAttribute('data-purchase-state') !== 'standard') return;
    var buyBtn = stickyRoot.querySelector('[data-sticky-buy]');
    var row = stickyRoot.querySelector('.pdp-sticky__row');
    var available = !!(variant && variant.available);
    if (buyBtn) {
      buyBtn.hidden = !available;
    }
    if (row) row.classList.toggle('pdp-sticky--soldout', !available);
    // Row height can change between the 2-button and solo layouts; keep the
    // body offset in sync if the bar is currently showing.
    if (stickyRoot.classList.contains('is-visible')) {
      document.body.style.setProperty('--pdp-sticky-height', stickyRoot.offsetHeight + 'px');
    }
  }

  // Thumb sliders, keyed by their section root, so showSlide() below can reach
  // the right one without threading an instance through every call site.
  var thumbSliders = new WeakMap();

  function showSlide(root, mediaId) {
    var slides = root.querySelectorAll('[data-gallery-slide]');
    var thumbs = root.querySelectorAll('[data-gallery-thumb]');
    var matched = false;
    slides.forEach(function (slide) {
      var isMatch = String(slide.getAttribute('data-media-id')) === String(mediaId);
      if (isMatch) matched = true;
      slide.hidden = !isMatch;
      slide.classList.toggle('is-active', isMatch);
    });
    if (!matched) return false;
    thumbs.forEach(function (thumb) {
      var isMatch = String(thumb.getAttribute('data-media-id')) === String(mediaId);
      thumb.classList.toggle('is-active', isMatch);
      if (isMatch) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    // AUTO-FOLLOW. This is the single place the thumb rail is asked to scroll:
    // whatever changed the active slide — a thumb click, a variant's
    // featured_media, or any navigation added later — arrives here first.
    var slider = thumbSliders.get(root);
    if (slider) slider.follow();
    return true;
  }

  function initGallery(root) {
    var thumbs = root.querySelectorAll('[data-gallery-thumb]');
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        showSlide(root, thumb.getAttribute('data-media-id'));
      });
    });
  }

  /* Stage arrows — overlay controls on the main image. They do not own any
     gallery state: like the thumbs and the variant picker they just call
     showSlide(), which is what keeps the thumb rail following along. */
  function initStageNav(root) {
    var prevBtn = root.querySelector('[data-stage-prev]');
    var nextBtn = root.querySelector('[data-stage-next]');
    if (!prevBtn && !nextBtn) return;

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-slide]'));
    if (slides.length < 2) return;

    function move(dir) {
      var current = 0;
      for (var i = 0; i < slides.length; i++) {
        if (!slides[i].hidden) {
          current = i;
          break;
        }
      }
      // Wraps, so neither arrow is ever a dead control.
      var target = (current + dir + slides.length) % slides.length;
      showSlide(root, slides[target].getAttribute('data-media-id'));
    }

    prevBtn && prevBtn.addEventListener('click', function () {
      move(-1);
    });
    nextBtn && nextBtn.addEventListener('click', function () {
      move(1);
    });
  }

  /* Thumb slider — a fixed viewport of exactly 5 thumbs with a track behind it
     that is moved by transform. Vertical beside the stage at >=993px,
     horizontal under it below that; ONE implementation, the axis is chosen per
     call from the same breakpoint the stylesheet uses. */
  function initThumbSlider(root) {
    var wrap = root.querySelector('[data-pdp-thumbs]');
    if (!wrap) return;

    var track = wrap.querySelector('[data-thumbs-track]');
    var items = Array.prototype.slice.call(wrap.querySelectorAll('[data-gallery-thumb]'));
    var prevBtn = wrap.querySelector('[data-thumbs-prev]');
    var nextBtn = wrap.querySelector('[data-thumbs-next]');
    var VISIBLE = 5;

    // <= VISIBLE thumbs: the markup rendered no chevrons and the CSS leaves the
    // viewport unsized. Bail before anything can write a transform, so the
    // static stack stays a static stack.
    if (!track || !wrap.classList.contains('pdp-thumbs--sliding') || items.length <= VISIBLE) {
      return;
    }

    // Index of the FIRST visible thumb, i.e. how far the track has slid.
    var index = 0;

    // The ONLY orientation branch in this file. Everything below — clamping,
    // auto-follow, drag-snap, the buttons — is written once and serves both.
    var verticalQuery = window.matchMedia('(min-width: 993px)');
    function isVertical() {
      return verticalQuery.matches;
    }

    // One thumb + one gap, and how many the viewport shows — all read from the
    // same custom properties the stylesheet sizes the viewport from, so the two
    // can never drift apart and neither orientation needs its own numbers.
    function step() {
      var cs = window.getComputedStyle(wrap);
      var size = parseFloat(cs.getPropertyValue('--thumb-size'));
      var gap = parseFloat(cs.getPropertyValue('--thumb-gap'));
      if (!size) return 0;
      return size + (gap || 0);
    }
    function visible() {
      var n = parseFloat(window.getComputedStyle(wrap).getPropertyValue('--thumb-visible'));
      return n > 0 ? n : VISIBLE;
    }

    // The track is (n * step - gap) long and the viewport shows visible() of
    // them, so the last valid first-visible index is n - visible(). Everything
    // that moves the track goes through clampIndex/clampOffset, which is what
    // keeps it from translating past either end.
    function maxIndex() {
      return Math.max(0, items.length - visible());
    }
    function clampIndex(i) {
      return Math.min(maxIndex(), Math.max(0, i));
    }
    function maxOffset() {
      return maxIndex() * step();
    }
    function clampOffset(px) {
      return Math.min(maxOffset(), Math.max(0, px));
    }

    function paint(offset) {
      track.style.transform = isVertical()
        ? 'translateY(' + -offset + 'px)'
        : 'translateX(' + -offset + 'px)';
    }

    // maxIndex() is 0 whenever the viewport already shows every thumb, at
    // whichever orientation is live; the chevrons are then taken out of the
    // layout rather than sitting permanently disabled.
    function syncButtons() {
      var spare = maxIndex() > 0;
      if (prevBtn) {
        prevBtn.hidden = !spare;
        prevBtn.disabled = index <= 0;
      }
      if (nextBtn) {
        nextBtn.hidden = !spare;
        nextBtn.disabled = index >= maxIndex();
      }
    }

    function goTo(i) {
      index = clampIndex(i);
      paint(index * step());
      syncButtons();
    }

    function activeIndex() {
      for (var i = 0; i < items.length; i++) {
        if (items[i].classList.contains('is-active')) return i;
      }
      return -1;
    }

    // Auto-follow: move the minimum distance that brings the active thumb into
    // the window [index, index + visible() - 1]; already inside means no move.
    function follow() {
      var a = activeIndex();
      if (a < 0) return;
      var span = visible();
      if (a < index) goTo(a);
      else if (a > index + span - 1) goTo(a - span + 1);
      else syncButtons();
    }

    prevBtn && prevBtn.addEventListener('click', function () {
      goTo(index - 1);
    });
    nextBtn && nextBtn.addEventListener('click', function () {
      goTo(index + 1);
    });

    /* Touch-drag, horizontal orientation only. The track follows the pointer
       within the clamped range, then snaps to the nearest thumb position. */
    var dragging = false;
    var startX = 0;
    var startOffset = 0;
    var travelled = 0;

    track.addEventListener('pointerdown', function (e) {
      // Drag is the horizontal affordance only; the vertical rail leaves
      // vertical gestures to the page.
      if (isVertical()) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dragging = true;
      travelled = 0;
      startX = e.clientX;
      startOffset = index * step();
      wrap.classList.add('is-dragging');
      if (track.setPointerCapture) track.setPointerCapture(e.pointerId);
    });

    track.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      travelled = Math.abs(dx);
      paint(clampOffset(startOffset - dx));
    });

    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      wrap.classList.remove('is-dragging');
      var dx = (e && typeof e.clientX === 'number' ? e.clientX : startX) - startX;
      // Snap to whichever thumb boundary the released offset is closest to.
      var s = step();
      goTo(s ? Math.round(clampOffset(startOffset - dx) / s) : 0);
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // A drag that ends over a thumb must not also select that thumb.
    track.addEventListener(
      'click',
      function (e) {
        if (travelled > 5) {
          e.preventDefault();
          e.stopPropagation();
          travelled = 0;
        }
      },
      true
    );

    // Crossing the breakpoint changes the axis, the step and how many thumbs
    // fit, so re-place the track from the index we already hold (clamped again
    // in goTo). paint() writes the whole transform, so the previous axis's
    // translate is replaced rather than left behind.
    function syncOrientation() {
      wrap.setAttribute('data-orientation', isVertical() ? 'vertical' : 'horizontal');
      goTo(index);
    }
    window.addEventListener('resize', syncOrientation);

    thumbSliders.set(root, { follow: follow });
    syncOrientation();
    follow();
  }

  function initAccordions(root) {
    var accordions = root.querySelectorAll('.pdp-accordion');
    accordions.forEach(function (accordion) {
      var trigger = accordion.querySelector('[data-accordion-trigger]');
      if (!trigger) return;
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      var icon = trigger.querySelector('[data-accordion-icon]');
      trigger.addEventListener('click', function () {
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!expanded));
        if (panel) panel.hidden = expanded;
        if (icon) icon.textContent = expanded ? '+' : '−';
      });
    });
  }

  function initQuantity(root) {
    var wrap = root.querySelector('.pdp-quantity');
    if (!wrap) return;
    var input = wrap.querySelector('[data-qty-input]');
    var minus = wrap.querySelector('[data-qty-minus]');
    var plus = wrap.querySelector('[data-qty-plus]');
    if (!input) return;
    if (minus) {
      minus.addEventListener('click', function () {
        var value = parseInt(input.value, 10) || 1;
        input.value = Math.max(1, value - 1);
      });
    }
    if (plus) {
      plus.addEventListener('click', function () {
        var value = parseInt(input.value, 10) || 1;
        input.value = value + 1;
      });
    }
  }

  // `buttons` is every add-to-cart trigger for this submit (main box +
  // sticky bar, when present) — they share one loading/success state since
  // they're driving the same form.
  function ajaxAddToCart(form, root, buttons) {
    var errorEl = root.querySelector('[data-form-error]');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    var originalLabels = buttons.map(function (btn) {
      return btn.textContent;
    });
    buttons.forEach(function (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding…';
    });

    var formData = new FormData(form);
    // Present only when the sticky Buy button's click handler added it for
    // a "Go to checkout" submit — see the sticky wiring note at the top of
    // this file.
    var returnTo = formData.get('return_to');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          throw new Error(result.data && result.data.description ? result.data.description : 'Unable to add to cart');
        }
        buttons.forEach(function (btn) {
          btn.textContent = 'Added ✓';
        });
        if (returnTo) {
          window.location.href = returnTo;
          return;
        }
        document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
        setTimeout(function () {
          buttons.forEach(function (btn, i) {
            btn.textContent = originalLabels[i];
            btn.disabled = false;
          });
        }, 1500);
      })
      .catch(function (error) {
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        }
        buttons.forEach(function (btn, i) {
          btn.textContent = originalLabels[i];
          btn.disabled = false;
        });
      });
  }

  function initVariants(root, productData, stickyRoot) {
    var form = root.querySelector('[data-product-form]');
    if (!form || !productData) return;

    var moneyFormat = window.Shopify && window.Shopify.money_format ? window.Shopify.money_format : '${{amount}}';
    var soldOutLabel = 'Sold out';
    var atc = root.querySelector('[data-add-to-cart]');
    if (atc) soldOutLabel = atc.getAttribute('data-label-soldout') || soldOutLabel;

    var stickyBuyBtn = stickyRoot ? stickyRoot.querySelector('[data-sticky-buy]') : null;
    var stickyBuyAction = stickyRoot ? stickyRoot.getAttribute('data-buy-action') : null;

    // Bound to the section container (not `form`) so variant-picker inputs
    // outside the <form> element — linked only via their form="..." HTML
    // attribute — are still caught. See file header comment.
    root.addEventListener('change', function (event) {
      if (!event.target.matches('.pdp-variants__input')) return;
      var selected = getSelectedOptions(root);
      var variant = getMatchingVariant(productData.variants, selected);

      // Sticky bar re-evaluates even when no variant matches (treated as
      // sold out); the main box below keeps its existing early-return.
      if (stickyRoot) updateStickyAvailability(stickyRoot, variant);

      if (!variant) return;

      updatePrice(root, variant, moneyFormat);
      updateButtons(root, variant, soldOutLabel);
      updateVariantId(root, variant);

      if (variant.featured_media) {
        showSlide(root, variant.featured_media.id);
      }

      // Inquire modal: keep the visible context line and the hidden field that
      // reaches the admin email in sync with the selected variant.
      updateInquireSku(root, variant);

      if (history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
    });

    // Inquire-only forms never get an add-to-cart path wired up in the first
    // place. This is the guard that actually matters: a capture-phase listener
    // added later could NOT reliably win, because listeners on the target element
    // fire in registration order regardless of the capture flag, and this
    // function runs before initNoPurchaseGuard().
    if (!form.hasAttribute('data-no-purchase')) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var buttons = [atc];
        if (stickyBuyBtn && !stickyBuyBtn.hidden) buttons.push(stickyBuyBtn);
        ajaxAddToCart(form, root, buttons.filter(Boolean));
      });
    }

    if (stickyBuyBtn) {
      stickyBuyBtn.addEventListener('click', function () {
        if (stickyBuyBtn.disabled || stickyBuyBtn.hidden) return;

        var returnInput = null;
        if (stickyBuyAction === 'checkout') {
          returnInput = document.createElement('input');
          returnInput.type = 'hidden';
          returnInput.name = 'return_to';
          returnInput.value = '/checkout';
          form.appendChild(returnInput);
        }

        // Submits the SAME form element as the main button — this fires
        // the 'submit' listener registered just above, not a second form.
        if (form.requestSubmit) {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }

        // The submit listener above already read this synchronously (via
        // `new FormData(form)` inside ajaxAddToCart) by the time
        // requestSubmit() returns, so it's safe to remove now.
        if (returnInput) returnInput.remove();
      });
    }
  }

  // Show: main buy-box add-to-cart button has scrolled out of the viewport.
  // Hide: the footer (.site-footer, from sections/footer-new.liquid — the
  // only footer section wired up via sections/footer-group.json) enters the
  // viewport, or the header's mobile nav drawer / search panel opens. Only
  // this file is touched, so those two open states are read the same way an
  // outside observer would: MutationObserver on the classes header-new.liquid
  // already toggles (body.nav-open, #header-search-new.is-open) — no event
  // bus to hook, and header-new.liquid isn't in scope to add one to.
  function initStickyVisibility(stickyRoot, root) {
    // The anchor is whatever the main buy box's primary CTA is: [data-add-to-cart]
    // in Standard mode, [data-inquire-anchor] (the Inquire button) in inquire-only
    // mode, where no ATC button is rendered. Standard-mode markup is unchanged —
    // data-inquire-anchor is only emitted in inquire-only mode, and ATC precedes
    // Inquire in document order regardless, so this resolves identically there.
    var mainBuyBtn = root.querySelector('[data-add-to-cart], [data-inquire-anchor]');
    var footerEl = document.querySelector('.site-footer');
    if (!mainBuyBtn || !('IntersectionObserver' in window)) return;

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var mainBtnOutOfView = false;
    var footerVisible = false;
    var overlayOpen = false;
    var hideTimer = null;

    function isDesktop() {
      return window.matchMedia('(min-width: 993px)').matches;
    }

    function shouldShow() {
      return !isDesktop() && mainBtnOutOfView && !footerVisible && !overlayOpen;
    }

    function showBar() {
      window.clearTimeout(hideTimer);
      stickyRoot.hidden = false;
      stickyRoot.removeAttribute('aria-hidden');
      // Force a reflow so the transform transition runs from the offscreen
      // state instead of jumping straight to translateY(0).
      void stickyRoot.offsetHeight;
      stickyRoot.classList.add('is-visible');
      document.body.classList.add('pdp-sticky-offset');
      document.body.style.setProperty('--pdp-sticky-height', stickyRoot.offsetHeight + 'px');
    }

    function hideBar(immediate) {
      stickyRoot.classList.remove('is-visible');
      stickyRoot.setAttribute('aria-hidden', 'true');
      window.clearTimeout(hideTimer);
      if (immediate || reduceMotion) {
        stickyRoot.hidden = true;
        document.body.classList.remove('pdp-sticky-offset');
      } else {
        // Keep the body offset through the slide-down so content doesn't
        // jump while the bar is still visibly covering it.
        hideTimer = window.setTimeout(function () {
          stickyRoot.hidden = true;
          document.body.classList.remove('pdp-sticky-offset');
        }, 250);
      }
    }

    function applyState() {
      if (shouldShow()) {
        showBar();
      } else {
        hideBar(overlayOpen || isDesktop());
      }
    }

    var buyBtnObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        mainBtnOutOfView = !entry.isIntersecting;
      });
      applyState();
    });
    buyBtnObserver.observe(mainBuyBtn);

    if (footerEl) {
      var footerObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          footerVisible = entry.isIntersecting;
        });
        applyState();
      });
      footerObserver.observe(footerEl);
    }

    var overlayObserver = new MutationObserver(function () {
      var navOpen = document.body.classList.contains('nav-open');
      var searchPanel = document.getElementById('header-search-new');
      var searchOpen = !!(searchPanel && searchPanel.classList.contains('is-open'));
      var nextOverlayOpen = navOpen || searchOpen;
      if (nextOverlayOpen !== overlayOpen) {
        overlayOpen = nextOverlayOpen;
        applyState();
      }
    });
    overlayObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    var searchPanelEl = document.getElementById('header-search-new');
    if (searchPanelEl) {
      overlayObserver.observe(searchPanelEl, { attributes: true, attributeFilter: ['class'] });
    }

    window.addEventListener('resize', applyState);
  }

  // Inquire-only guard. A plain, action-less <form> is still rendered (the variant
  // picker is bound to it via form="..."), carrying onsubmit="return false".
  // This is the last of the four layers: it catches a submit dispatched by
  // something that bypassed the inline attribute (a browser extension, injected
  // script, or a sanitiser that stripped inline handlers under CSP).
  // NOTE: it is NOT what stops add-to-cart — initVariants above simply never
  // registers that handler on a [data-no-purchase] form.
  function initNoPurchaseGuard(root) {
    root.querySelectorAll('form[data-no-purchase]').forEach(function (form) {
      form.addEventListener(
        'submit',
        function (event) {
          event.preventDefault();
          event.stopImmediatePropagation();
        },
        true
      );
    });
  }

  function initSection(root) {
    var jsonEl = root.querySelector('[data-product-json]');
    var productData = null;
    if (jsonEl) {
      try {
        productData = JSON.parse(jsonEl.textContent);
      } catch (e) {
        productData = null;
      }
    }

    initGallery(root);
    initStageNav(root);
    // Before initVariants below, which can call showSlide() during setup.
    initThumbSlider(root);
    initAccordions(root);
    initQuantity(root);

    var sectionId = root.getAttribute('data-section-id');
    var stickyRoot = sectionId
      ? document.querySelector('[data-pdp-sticky][data-section-id="' + sectionId + '"]')
      : null;

    // Before initVariants, so this listener is registered first on the form.
    initNoPurchaseGuard(root);
    if (productData) initVariants(root, productData, stickyRoot);
    initInquire(root);
    if (stickyRoot) initStickyVisibility(stickyRoot, root);
  }

  /* -------------------------------------------------------------------------
   * Inquire modal
   * -------------------------------------------------------------------------
   * Focus trapping is NOT reimplemented here: assets/global.js already ships
   * trapFocus(container, elementToFocus) / removeTrapFocus(elementToFocus) as
   * globals, loaded in <head> on every page, and that is what the cart drawer,
   * cart notification and details-modal all use. This calls the same pair, with
   * a no-op guard so the modal still opens if global.js ever stops loading.
   *
   * The modal is a SIBLING of the .pdp root (it sits outside it in the Liquid),
   * so it is looked up by the shared data-section-id, the same way the sticky
   * bar is.
   * ---------------------------------------------------------------------- */

  function inquireModalFor(root) {
    var id = root.getAttribute('data-section-id');
    return document.querySelector('[data-inquire-modal][data-section-id="' + id + '"]');
  }

  function updateInquireSku(root, variant) {
    var modal = inquireModalFor(root);
    if (!modal || !variant) return;
    var sku = variant.sku || '';
    var line = modal.querySelector('[data-inquire-sku]');
    var input = modal.querySelector('[data-inquire-sku-input]');
    // Visible line follows the reference's "SKU 10-10-36088" (no colon). The
    // hidden field still carries the raw SKU, unchanged, so the emailed payload
    // is byte-identical to before.
    if (line) line.textContent = sku ? 'SKU ' + sku : '';
    if (input) input.value = sku;

    renderInquirePrice(modal, variant);

    // The redesign puts the product photo in the modal's context rail. Without
    // this it would go stale next to a SKU that updates, so it moves with the
    // variant — same hook, same call site.
    var img = modal.querySelector('[data-inquire-image]');
    if (img && variant.featured_image && variant.featured_image.src) {
      img.src = variant.featured_image.src;
      if (variant.featured_image.alt) img.alt = variant.featured_image.alt;
    }
  }

  // Same rule as snippets/pdp-inquire-price.liquid: a 0 price never shows
  // (the "Text when price is 0" setting, carried on data-zero-text, replaces it
  // when set); a sale shows the compare-at price struck through. The hidden
  // contact[price] field gets plain text, "Price on request" when 0.
  function renderInquirePrice(modal, variant) {
    var line = modal.querySelector('[data-inquire-price]');
    var input = modal.querySelector('[data-inquire-price-input]');
    var moneyFormat = window.Shopify && window.Shopify.money_format ? window.Shopify.money_format : '${{amount}}';
    var price = variant.price || 0;
    var onSale = price > 0 && variant.compare_at_price > price;

    if (line) {
      line.textContent = '';
      if (price > 0) {
        var current = document.createElement('span');
        current.setAttribute('data-inquire-price-current', '');
        current.textContent = formatMoney(price, moneyFormat);
        line.appendChild(current);
        if (onSale) {
          var compare = document.createElement('s');
          compare.setAttribute('data-inquire-price-compare', '');
          compare.textContent = formatMoney(variant.compare_at_price, moneyFormat);
          line.appendChild(compare);
        }
      } else if (line.getAttribute('data-zero-text')) {
        var text = document.createElement('span');
        text.className = 'pdp-inquire__product-price-text';
        text.textContent = line.getAttribute('data-zero-text');
        line.appendChild(text);
      }
    }
    if (input) {
      input.value = price > 0
        ? formatMoney(price, moneyFormat) + (onSale ? ' (was ' + formatMoney(variant.compare_at_price, moneyFormat) + ')' : '')
        : 'Price on request';
    }
  }

  function initInquire(root) {
    var modal = inquireModalFor(root);
    if (!modal) return;

    // Both Inquire buttons — buy box and sticky bar — carry the same hook and
    // land in the same open(). The sticky one lives outside `root`, so the
    // lookup is document-wide and filtered by section id.
    var sectionId = root.getAttribute('data-section-id');
    var openers = Array.prototype.filter.call(
      document.querySelectorAll('[data-inquire-open]'),
      function (btn) {
        var owner = btn.closest('[data-product-info-section], [data-pdp-sticky]');
        return !owner || owner.getAttribute('data-section-id') === sectionId;
      }
    );
    var overlay = modal.querySelector('[data-inquire-overlay]');
    var closeBtn = modal.querySelector('[data-inquire-close]');
    var form = modal.querySelector('[data-inquire-form]');
    var lastTrigger = null;

    function firstFocusable() {
      return (
        modal.querySelector('.pdp-inquire__success') ||
        modal.querySelector('input:not([type="hidden"]):not([tabindex="-1"]), textarea, button') ||
        closeBtn
      );
    }

    // .is-open is the ONLY state flag now. The `hidden` attribute is gone from
    // the markup: this modal's own `display: flex` out-ranks the UA stylesheet's
    // [hidden]{display:none}, which is what left the closed overlay covering the
    // page. The closed state is visibility:hidden + pointer-events:none in CSS,
    // which nothing overrides — and, unlike display:none, it leaves the element
    // laid out, so the overlay/panel already have a computed opacity 0 to
    // transition FROM. No forced reflow is needed here (the mega menu needs one
    // only because .is-open is what flips it out of display:none).
    function isOpen() {
      return modal.classList.contains('is-open');
    }

    function open(trigger) {
      if (isOpen()) return;
      lastTrigger = trigger || null;
      // A second inquiry starts on the form, never on the previous
      // thank-you panel. No-op on a first open.
      showForm();
      document.body.classList.add('pdp-inquire-open');
      modal.classList.add('is-open');
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      if (typeof trapFocus === 'function') trapFocus(modal, firstFocusable());
      else { var f = firstFocusable(); f && f.focus(); }
    }

    function close() {
      if (!isOpen()) return;
      // Dropping .is-open releases pointer-events and starts the fade in the
      // same frame; visibility flips to hidden 0.25s later via the CSS
      // transition-delay, so nothing is left interactive in the meantime and
      // there is no timer to get out of sync with the class.
      modal.classList.remove('is-open');
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      if (typeof removeTrapFocus === 'function') removeTrapFocus(lastTrigger || undefined);
      else if (lastTrigger) lastTrigger.focus();
      document.body.classList.remove('pdp-inquire-open');
    }

    openers.forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        open(btn);
      });
    });
    closeBtn && closeBtn.addEventListener('click', close);
    overlay && overlay.addEventListener('click', close);

    // Phones only. The sheet scrolls internally, so when the soft keyboard opens
    // the focused field can end up behind it. The browser scrolls the field into
    // view on its own only sometimes (and not at all when the keyboard resizes
    // the visual viewport rather than the layout viewport), so it is asked
    // explicitly, after the keyboard animation has settled. 'nearest' means a
    // field already in view is not yanked around.
    modal.addEventListener('focusin', function (event) {
      if (!window.matchMedia('(max-width: 599px)').matches) return;
      var field = event.target;
      if (!field || !field.matches || !field.matches('input, textarea')) return;
      setTimeout(function () {
        if (typeof field.scrollIntoView === 'function') {
          field.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 300);
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen()) close();
    });

    /* ---- AJAX submit -------------------------------------------------------
     * Posts the same FormData to the same {% form 'contact' %} endpoint and
     * swaps the body for the success panel in place. Success is INFERRED from
     * the response, so the rules below are deliberately conservative: anything
     * that is not a positively-identified success is treated as a failure the
     * shopper is told about, never as a silent win.
     *
     * See the "brittleness" note in the section's Liquid: the markers are
     * [data-inquire-posted] (server success token) and a non-empty
     * [data-inquire-errors] container, both rendered by this theme's own
     * Liquid, plus Shopify's ?contact_posted=true redirect param.
     * -------------------------------------------------------------------- */

    var submitBtn = modal.querySelector('[data-inquire-submit]');
    var bodyPanel = modal.querySelector('[data-inquire-body]');
    var successPanel = modal.querySelector('[data-inquire-success]');
    var errorBox = modal.querySelector('[data-inquire-errors]');
    var noticeBox = modal.querySelector('[data-inquire-notice]');
    // Double-submit guard. Checked before anything else in the handler, and the
    // button is disabled too — belt and braces, because Enter-in-a-field can
    // fire submit without the button being involved at all.
    var pending = false;

    // Shopify's error keys -> this form's field names.
    var ERROR_FIELD_MAP = {
      email: 'contact[email]',
      body: 'contact[body]',
      phone: 'contact[phone]',
      author: 'contact[first_name]',
      name: 'contact[first_name]'
    };

    function setPending(on) {
      pending = on;
      modal.classList.toggle('is-submitting', on);
      if (!submitBtn) return;
      submitBtn.disabled = on;
      if (on) submitBtn.setAttribute('aria-disabled', 'true');
      else submitBtn.removeAttribute('aria-disabled');
    }

    function clearErrors() {
      if (errorBox) {
        errorBox.textContent = '';
        errorBox.hidden = true;
        errorBox.removeAttribute('data-error-fields');
      }
      if (window.FormRelay) window.FormRelay.clearFieldErrors(form);
      Array.prototype.forEach.call(
        modal.querySelectorAll('[aria-invalid="true"]'),
        function (el) { el.removeAttribute('aria-invalid'); }
      );
    }

    function markInvalidFields(keys) {
      if (!keys) return;
      keys.split(',').forEach(function (raw) {
        var key = raw.trim();
        var name = ERROR_FIELD_MAP[key];
        if (!name) return;
        var field = form.querySelector('[name="' + name + '"]');
        if (field) field.setAttribute('aria-invalid', 'true');
      });
    }

    // `node` is an element from the PARSED response document, so its markup is
    // adopted rather than re-authored — Shopify's own wording survives intact.
    function showErrorNode(node) {
      if (!errorBox) return;
      errorBox.textContent = '';
      Array.prototype.forEach.call(node.childNodes, function (child) {
        errorBox.appendChild(document.importNode(child, true));
      });
      errorBox.hidden = false;
      markInvalidFields(node.getAttribute('data-error-fields'));
      errorBox.scrollIntoView({ block: 'nearest' });
    }

    function showErrorText(message) {
      if (!errorBox) return;
      errorBox.textContent = message;
      errorBox.hidden = false;
      errorBox.scrollIntoView({ block: 'nearest' });
    }

    function showSuccess() {
      if (bodyPanel) bodyPanel.hidden = true;
      if (successPanel) successPanel.hidden = false;
      // Reset quietly BEHIND the success view so reopening starts clean. The
      // hidden product/SKU inputs are re-populated by their own hook, and
      // form.reset() restores their markup defaults rather than blanking them.
      try { form.reset(); } catch (e) {}
      clearErrors();
      if (noticeBox) { noticeBox.textContent = ''; noticeBox.hidden = true; }
      // trapFocus() snapshots its first/last focusable element at call time and
      // does not observe the DOM (see assets/global.js) — after this swap that
      // snapshot points at detached nodes, so the trap is re-armed against the
      // new content. Without this, Tab escapes the modal.
      if (typeof trapFocus === 'function') trapFocus(modal, closeBtn || successPanel);
      else if (closeBtn) closeBtn.focus();
    }

    // Back to the form view. Called on open() so a second inquiry after a
    // success never lands on the stale thank-you panel.
    function showForm() {
      if (successPanel) successPanel.hidden = true;
      if (bodyPanel) bodyPanel.hidden = false;
    }

    function isChallenge(responseUrl, doc) {
      if (responseUrl && responseUrl.indexOf('/challenge') !== -1) return true;
      return !!(
        doc.querySelector('.shopify-challenge__container') ||
        doc.querySelector('form[action*="/challenge"]') ||
        doc.querySelector('#g-recaptcha, .g-recaptcha')
      );
    }

    // Honest fallback: hand the shopper to Shopify's real challenge page by
    // re-submitting natively. form.submit() does NOT fire submit listeners, so
    // this cannot be re-intercepted and cannot loop. The submission is never
    // faked as sent and never silently dropped.
    function goToChallenge() {
      if (noticeBox) {
        noticeBox.textContent =
          'Please complete a quick verification — continuing now.';
        noticeBox.hidden = false;
      }
      // Left disabled while the browser navigates to the challenge. If that
      // navigation does not happen — blocked popup-style heuristics, a
      // beforeunload veto, an extension — the modal would otherwise sit locked
      // with a dead button and no way to retry, so a safety net releases it.
      form.submit();
      setTimeout(function () {
        if (!pending) return;
        setPending(false);
        if (noticeBox) {
          noticeBox.textContent =
            'We could not open the verification step. Please try sending again.';
        }
      }, 4000);
    }

    form && form.addEventListener('submit', function (event) {
      event.preventDefault();
      handleSubmit();
    });
    // Form relay on: assets/form-relay.js stops the native submit event (so Shopify's hCaptcha
    // listener doesn't post it too) and sends this instead.
    form && form.addEventListener('form-relay:submit', handleSubmit);

    function handleSubmit() {
      if (pending) return;

      // Native constraint validation runs FIRST and gates the fetch, so the
      // browser's own field messages are what the shopper sees for empty or
      // malformed required fields. Nothing is posted until this passes.
      if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

      clearErrors();
      if (noticeBox) { noticeBox.textContent = ''; noticeBox.hidden = true; }
      setPending(true);

      // Form relay (Theme settings → Forms): posts form_id product-inquiry to the relay. If the
      // relay can't be reached (network, 15 s timeout, 5xx), the Shopify post below runs as before.
      if (window.FormRelay && form.getAttribute('data-relay-form') && form.getAttribute('data-relay-url')) {
        window.FormRelay.send(form).then(function (result) {
          if (result.status === 'ok') {
            setPending(false);
            form.dispatchEvent(new CustomEvent('form-relay:success', {
              bubbles: true,
              detail: { formId: result.payload.form_id }
            }));
            showSuccess();
            return;
          }
          if (result.status === 'fallback') {
            postToShopify();
            return;
          }
          setPending(false);
          if (result.status === 'fields') {
            var first = window.FormRelay.markFields(form, result.fields);
            showErrorText(result.message || window.FormRelay.GENERIC_ERROR);
            if (first) first.focus();
            return;
          }
          showErrorText(result.message);
        });
        return;
      }

      postToShopify();
    }

    // The Shopify contact post (AJAX), unchanged: the only path when the relay is off, and the
    // relay's fallback.
    function postToShopify() {
      var action = form.getAttribute('action') || '/contact';

      fetch(action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' }
      })
        .then(function (response) {
          return response.text().then(function (html) {
            return { ok: response.ok, url: response.url || '', html: html };
          });
        })
        .then(function (res) {
          var doc = new DOMParser().parseFromString(res.html, 'text/html');

          // 1. Spam challenge — hand off, do not guess.
          if (isChallenge(res.url, doc)) {
            goToChallenge();
            return;
          }

          // 2. Server-rendered validation errors win over everything below.
          var errNode = doc.querySelector('[data-inquire-errors]');
          if (errNode && errNode.textContent.trim()) {
            showErrorNode(errNode);
            setPending(false);
            return;
          }

          // 3. Positively-identified success only.
          var posted =
            res.url.indexOf('contact_posted=true') !== -1 ||
            !!doc.querySelector('[data-inquire-posted]');
          if (res.ok && posted) {
            setPending(false);
            showSuccess();
            return;
          }

          // 4. Ambiguous: a 4xx/5xx, or a 200 with neither marker. Not a
          //    success. The shopper is told, and the button comes back.
          showErrorText(
            "We couldn't confirm your inquiry was sent. Please try again, or call us using the number in this window."
          );
          setPending(false);
        })
        .catch(function () {
          // Network/CORS/abort. Retry-friendly wording; button re-enabled.
          showErrorText(
            "We couldn't reach the server. Please check your connection and try again."
          );
          setPending(false);
        });
    }

    // Landing back here after the native challenge round-trip (or any native
    // submit): the server token is present, so show the confirmation rather
    // than a blank form. This is a SERVER-token check, not the deleted
    // #inquire-sent hash mechanism.
    if (modal.querySelector('[data-inquire-posted]')) open(null);
  }

  document.querySelectorAll('[data-product-info-section]').forEach(initSection);
})();
